/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	useContext,
	useEffect,
	useMemo,
	useState,
	useRef,
	forwardRef,
	Fragment,
} from 'react';

import { css, jsx } from '@compiled/react';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import hash from 'object-hash';
import { ReactRelayContext } from 'react-relay';
import { useMergeRefs } from 'use-callback-ref';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';
import type { ForgeContextToken } from '@atlassian/forge-ui-types/context';

import {
	fetchUserRecommendations,
	type FetchUserRecommendationsPayload,
	type FetchUserRecommendationsResponse,
} from '../../bridge/methods/fetchUserRecommendations';
import {
	type RequestTeamworkGraphPayload,
	type RequestTeamworkGraphResponse,
	TEAMWORK_GRAPH_GATEWAY_PATH,
} from '../../bridge/methods/requestTeamworkGraph';
import { ThreeLOPrompt } from '../../components';
import { EnvironmentContext } from '../../context';
import { useTracingContext } from '../../error-reporting';
import { useResizeAnalytics } from '../../performance-profiling/useResizeAnalytics';
import { SANDBOX_DEPRECATION_ERROR } from '../../renderer/errorMessages/errorTypes';
import { isDeveloperSpaceError } from '../../renderer/errorMessages/errorTypes';
import { isCustomUI, parseExtensionId } from '../../utils';
import { isBodiedLayout } from '../../utils/getExtensionType';
import { egressPermissionService } from '../../utils/useEgressPermissionService';
import {
	type SubscribeRealtimeChannelPayload,
	type PublishRealtimeChannelPayload,
	useSubscribeRealtimeChannel,
	usePublishRealtimeChannel,
	useEmitPublicEvent,
	useInvokeExtension,
} from '../../web-client';
import { emitFrontendCustomMetric } from '../../web-client/queries/emitFrontendCustomMetric';
import { emitFrontendLogs } from '../../web-client/queries/emitFrontendLogs';
import { getFeatureFlags } from '../../web-client/queries/getFeatureFlags';
import {
	useBridge,
	useProductFetchClient,
	useNavigation,
	createBridgeApi,
	getProductDomain,
	isBinaryContentType,
	useRovoAgentsChat,
	usePermission,
	type NavigatePayload,
} from '../bridge';
import {
	PERMISSION_METHOD_PREFIX,
	permissionMethods,
} from '../bridge/methods/permissions/usePermission';
import { useRemoteFetchClient } from '../bridge/methods/useRemoteFetchClient';
import { isAppConsentedLocally, setAppConsentLocal } from './localAppConsent';
import type { InnerIframeProps, ComponentMap, HTMLCustomIFrameElement } from './types';
import { useKeepIframeInteractiveDuringDrag } from './useKeepIframeInteractiveDuringDrag';
import { getBrowserLocale, getBrowserTimezone, getResolverContext, getViewContext } from './utils';
import { getLoadingComponent } from './utils/getLoadingComponent';

type InvokeRetry = () => Promise<void>;

let postRobotOn: any = null;
if (typeof window !== 'undefined') {
	postRobotOn = require('@atlassian/bridge-core').postRobotOn;
}

const styles = cssMap({
	threeLOInfo: {
		width: '100%',
		height: '100%',
	},
	box: {
		position: 'relative',
	},
});

// TODO: no styles defined for resizable modals yet.
// Only Jira enables resizing on modals at the moment.
const modalIframeStyles = css({
	height: '100%',
});

// Pre-`forge-ui-modal-rounded-corners` behaviour: a 3px radius that doesn't match the modal's
// `radius.xlarge`. Kept so the gate-off path is unchanged.
const legacyModalRadiusStyles = css({
	borderRadius: token('radius.small', '3px'),
});

const iframeStyles = css({
	border: 'none',
	display: 'block',
	width: '100%',
});

const hiddenIframeStyles = css({
	display: 'none',
});

const nonResizeableIframeStyles = css({
	height: '100%',
});

const defaultComponents: ComponentMap = {
	ThreeLOPrompt: (props) => <ThreeLOPrompt {...props} />,
};

/**
 * Hook for determining default IFrame React ref, using parent ref when provided
 *
 * @param parentIframeRef parent IFrame React ref
 * @returns default IFrame React ref
 */
function useDefaultIframeRef(parentIframeRef: React.Ref<HTMLCustomIFrameElement>) {
	const localIframeRef = useRef<HTMLCustomIFrameElement>(null);
	const mergedRef = useMergeRefs([parentIframeRef, localIframeRef]);
	return mergedRef;
}

export const InnerIframe: React.ForwardRefExoticComponent<
	InnerIframeProps & React.RefAttributes<HTMLCustomIFrameElement>
> = forwardRef<HTMLCustomIFrameElement, InnerIframeProps>(
	(
		{
			accountId,
			extension,
			contextIds,
			components,
			coreData,
			extensionData,
			extensionViewData,
			loadingComponent,
			onLoad,
			height = '100%',
			width = '100%',
			src,
			bridge,
			customBridgeMethods,
			isResizable,
			getContextToken,
			timezone,
			locale,
			setIsThreeLOPromptVisible,
			setShouldShowRenderer,
			extensionPayload,
			isHidden,
			theme,
			surfaceColor,
			appId,
			isInModal,
			setIsLoading,
			onError,
			entryPoint,
			isEmbeddedMacro,
			embeddedMacroParentType,
			embeddedParentLocalId,
		},
		parentIframeRef,
	) => {
		const { ThreeLOPrompt } = components ? components(defaultComponents) : defaultComponents;

		const appIsAuthenticated = isAppConsentedLocally(appId, accountId);
		const {
			appVersion,
			consentUrl,
			currentUserConsent,
			requiresUserConsent,
			properties: { frameId, layout },
		} = extension;
		const [userConsentRequired, setUserConsentRequired] = useState(
			consentUrl && !currentUserConsent && requiresUserConsent && !appIsAuthenticated,
		);

		const productContext = useMemo(() => {
			const resolverContext = getResolverContext({ ...coreData, appVersion }, extensionData);
			const viewContext = getViewContext({
				resolverContext,
				accountId,
				extension,
				timezone: timezone ?? getBrowserTimezone(),
				locale: locale ?? getBrowserLocale(),
				extensionPayload,
				extensionViewData,
				theme,
				surfaceColor,
				location: window.location.toString(),
			});
			return { resolverContext, viewContext };
		}, [
			appVersion,
			coreData,
			extensionData,
			extensionViewData,
			accountId,
			extension,
			timezone,
			locale,
			extensionPayload,
			theme,
			surfaceColor,
		]);

		// For embedded macros, create context token options to pass to getContextToken
		// This ensures the token is generated for the embedded macro's context, not the parent's
		const embeddedMacroContextTokenOptions = (
			fg('platform_forge_ui_refactor_macro_parent_type')
				? !!embeddedMacroParentType
				: isEmbeddedMacro
		)
			? {
					extensionOverride: {
						extension,
						localId: coreData.localId,
					},
				}
			: undefined;

		/**
		 * Wrapper for getContextToken that handles both embedded macro and regular cases.
		 * For embedded macros, includes the extension override to generate a token scoped to the embedded macro.
		 * Falls back to cached token when getContextToken is not available.
		 */
		const getContextTokenWrapper = async (
			contextIdsOverride?: string[],
		): Promise<string | undefined> => {
			if (!getContextToken) {
				return contextTokenRef.current?.jwt;
			}

			if (
				fg('platform_forge_ui_refactor_macro_parent_type')
					? !!embeddedMacroParentType
					: isEmbeddedMacro
			) {
				return getContextToken({
					extensionOverride: {
						extension,
						localId: coreData.localId,
					},
					...(contextIdsOverride && { contextIdsOverride }),
				});
			}

			return contextIdsOverride ? getContextToken({ contextIdsOverride }) : getContextToken();
		};

		const tracing = useTracingContext();

		const contextTokenRef = useRef<ForgeContextToken | undefined>(undefined);

		const [threeLOInfo, setThreeLOInfo] = useState<{
			authUrl: string;
			onReject: () => void;
			onSuccess: () => Promise<void>;
		} | null>(null);

		const invokeRetriesRef = useRef<Array<InvokeRetry>>([]);

		const iframeRef = useDefaultIframeRef(parentIframeRef);

		// Modals disable pointer events on every cross origin iframe while a drag
		// operation is in progress. That must not apply to a modal's own content.
		const keepInteractiveRef = useKeepIframeInteractiveDuringDrag({
			isEnabled: fg('forge-ui-modal-iframe-drag-fix'),
			isInModal,
		});
		const mergedIframeRef = useMergeRefs<HTMLCustomIFrameElement>([iframeRef, keepInteractiveRef]);

		useEffect(() => {
			const clearThreeLOInfo = async () => setThreeLOInfo(null);

			if (userConsentRequired) {
				tracing?.recordConsentFlow('consent flow - start');

				setThreeLOInfo({
					authUrl: consentUrl!,
					onSuccess: async () => {
						tracing?.recordConsentFlow('consent flow - end', 'success');
						setUserConsentRequired(false);
						setIsThreeLOPromptVisible(false);
						setAppConsentLocal(appId, accountId);
						return clearThreeLOInfo();
					},
					onReject: () => {
						tracing?.recordConsentFlow('consent flow - end', 'rejected');
						return clearThreeLOInfo();
					},
				});
				setIsThreeLOPromptVisible(true);
				setShouldShowRenderer?.(false);

				if (onLoad) {
					onLoad();
				}
			}
		}, [
			onLoad,
			setIsThreeLOPromptVisible,
			setShouldShowRenderer,
			tracing,
			appId,
			accountId,
			consentUrl,
			userConsentRequired,
		]);

		const navigation = useNavigation({
			extension,
			push: bridge?.spaPush,
			appId,
			onNavigate: bridge?.onNavigate,
			openNewTabAsPopupWindow: bridge?.openNewTabAsPopupWindow,
		});

		const navigationRef = useRef(navigation);
		navigationRef.current = navigation;

		// Skip FAUS navigate listener setup when this is the UI Kit macro custom config view (current resource is the config resource).
		const isMacroConfigView = Boolean(
			extension.properties?.config?.resource &&
			extension.properties?.resource === extension.properties?.config?.resource,
		);

		// Listen for navigate events from FAUS (e.g. link clicks in forge-adf-renderer). Skip when in modal or UI Kit macro custom config so only the macro body registers.
		useEffect(() => {
			if (
				isBodiedLayout(layout) &&
				coreData.localId &&
				postRobotOn &&
				!isInModal &&
				!isMacroConfigView &&
				fg('platform_faus_navigate_to_product')
			) {
				const allowedDomain = getProductDomain();
				try {
					const listener = postRobotOn(
						`navigateFromEmbeddedFAUS_${coreData.localId}`,
						{ domain: allowedDomain },
						async ({ data }: { data: NavigatePayload }) => {
							if (!data?.url) {
								return;
							}
							navigationRef.current.onNavigate({ type: 'new-tab', url: data.url }).catch(() => {
								// User cancelled external link modal or invalid link
							});
						},
					);

					return () => {
						listener.cancel();
					};
					// eslint-disable-next-line no-unused-vars
				} catch (_) {
					// eslint-disable-next-line no-console
					console.log('Failed to set up navigation listener');
				}
			}
		}, [layout, coreData.localId, isInModal, isMacroConfigView]);

		const invoker = useInvokeExtension(contextIds, extension.id);

		// The hash generated here should match the one created in the Realtime service when it hashes extension.context
		// in the FCT.
		// If this is flaky because of differences between extensionData and the FCT's extension.context, then
		// use the decoded FCT context directly here as well.
		const extensionContext = (() => {
			try {
				const {
					// eslint-disable-next-line no-unused-vars
					modal,
					...productExtensionData
				} = extensionData;
				return hash(productExtensionData || {});
				// eslint-disable-next-line no-unused-vars
			} catch (_) {
				// eslint-disable-next-line no-console
				console.error('Product context is not a serializable object.');
				return '';
			}
		})();

		// the realtime hooks below have no side effect (i.e. does not actively trigger any network calls or actively makes any subscription).
		// It is because everything inside this hook is lazily initialized.
		// Importantly:
		// - SubscribeRealtimeChannel is called by the client (i.e. @forge/bridge)
		const subscribeRealtimeChannel = useSubscribeRealtimeChannel(
			extension.installationId,
			extensionContext,
			getContextToken,
			embeddedMacroContextTokenOptions,
		);

		const publishRealtimeChannel = usePublishRealtimeChannel(
			extension.installationId,
			extensionContext,
			appId,
		);

		const renderThreeLOPrompt = (retry: () => Promise<void>) => {
			return new Promise<void>((resolve, reject) => {
				const authUrl = threeLOInfo?.authUrl || consentUrl;
				if (authUrl) {
					invokeRetriesRef.current.push(retry);

					tracing?.recordConsentFlow('consent flow - start');

					setThreeLOInfo({
						authUrl,
						onSuccess: () => {
							tracing?.recordConsentFlow('consent flow - end', 'success');
							resolve();
							setThreeLOInfo(null);
							setIsThreeLOPromptVisible(false);
							setShouldShowRenderer?.(true);
							return Promise.all(invokeRetriesRef.current.map((retry) => retry())).then(() => {
								invokeRetriesRef.current = [];
							});
						},
						onReject: () => {
							tracing?.recordConsentFlow('consent flow - end', 'rejected');
							return reject();
						},
					});
					setIsThreeLOPromptVisible(true);
					setShouldShowRenderer?.(false);
				}
			});
		};

		const environment = useContext(EnvironmentContext);
		const relayEnvironment = useContext(ReactRelayContext)?.environment;
		const { cloudId, workspaceId } = coreData;
		const rovoChat = useRovoAgentsChat(
			extension.type,
			appId,
			cloudId,
			extension.installationId,
			extension.environmentId,
		);

		const isAnonymousAccessSupportedByApp =
			extension.properties?.unlicensedAccess?.includes('anonymous') ?? false;
		const productFetchClient = useProductFetchClient({
			appId,
			environment,
			contextIds,
			cloudId,
			workspaceId,
			extensionId: extension.id,
			renderThreeLOPrompt,
			isAnonymousAccessSupportedByApp,
		});

		const permissions = usePermission({
			appId,
			installationId: extension.installationId,
			contextIds,
			extensionId: extension.id,
			renderThreeLOPrompt,
		});

		const emitPublicEvent = useEmitPublicEvent(contextIds[0], {
			appId: extension.appId,
			id: extension.id.split('/').slice(0, 3).join('/'),
			installationId: extension.installationId,
		});

		const fetchRemoteClient = useRemoteFetchClient();

		const permissionFunctions = Object.fromEntries(
			permissionMethods.map((method) => [
				`${PERMISSION_METHOD_PREFIX}${method}`,
				permissions[method],
			]),
		);

		const {
			loading,
			iframeProps: { onLoad: onLoadIframeFromProps },
		} = useBridge({
			cloudId,
			theme,
			surfaceColor,
			origin: new URL(src).origin,
			api: {
				...createBridgeApi(bridge, {
					userConsentRequired: !!userConsentRequired,
				}),
				openRovo: rovoChat.openRovo,
				onInvoke: async (payload) => {
					const contextToken = await getContextTokenWrapper(contextIds);

					tracing?.recordGqlCall('invokeExtension - start');

					const { data } = await invoker({
						call: payload,
						context: {
							...productContext.resolverContext,
						},
						extensionPayload,
						contextToken,
					});

					// Default to using the regular invocation response
					var resp = data?.invokeExtension;

					if (!resp?.success) {
						const error = resp?.errors?.[0];
						if (error?.extensions.errorType === 'USER_CONSENT_REQUIRED') {
							return {
								type: '3lo',
								authUrl: error?.extensions.fields?.authInfoUrl as string,
							};
						}
						if (
							(error?.extensions.errorType === SANDBOX_DEPRECATION_ERROR ||
								isDeveloperSpaceError(error?.extensions.errorType as string)) &&
							onError
						) {
							onError(error?.extensions.errorType);
						}
						return {
							type: 'err',
							message: error?.message ?? 'An error occurred while trying to invoke.',
						};
					}

					tracing?.recordGqlCall('invokeExtension - end');

					contextTokenRef.current = resp?.contextToken;
					return {
						type: 'ok',
						response: resp?.response?.body,
						metadata: {
							rateLimitProperties: resp?.rateLimitProperties,
						},
					};
				},
				onInitFeatureFlags: async (data) => {
					const contextToken = await getContextTokenWrapper();

					tracing?.recordBridgeCall('getFeatureFlags - start');

					try {
						const resp = await getFeatureFlags(
							appId,
							data.user,
							data.config,
							environment,
							contextToken,
						);
						tracing?.recordBridgeCall('getFeatureFlags - end');

						return {
							type: 'ok',
							response: resp,
						};
					} catch (err) {
						tracing?.recordBridgeCall('getFeatureFlags - end');
						// eslint-disable-next-line no-console
						console.error('There were errors while initialising feature flags client ', err);
						return {
							type: 'err',
							message: 'An error occurred while trying to initialise feature flags client.',
						};
					}
				},

				onEmitFrontendCustomMetric: async (data) => {
					// Bail without a context ARI or Relay env. `contextIds[0]` is typed `string` but is `undefined` for an empty array.
					const contextAri = contextIds[0];
					if (!contextAri || !relayEnvironment) {
						return;
					}

					tracing?.recordBridgeCall('emitFrontendCustomMetric - start');

					try {
						// `extension.type` is the module *type* (e.g. `jira:issuePanel`); the module *key* is the
						// last segment of the extension ARI — parse it the same way Iframe.tsx does.
						const { moduleKey } = parseExtensionId(extension.id);
						await emitFrontendCustomMetric({
							relayEnvironment,
							contextAri,
							appId,
							installationId: extension.installationId,
							moduleKey,
							customMetricName: data.customMetricName,
							value: data.value,
						});
					} catch {
						// Silent fail: emitting a metric must never surface an error to the app.
					}

					tracing?.recordBridgeCall('emitFrontendCustomMetric - end');
				},

				onEmitFrontendLogs: async (data) => {
					const contextAri = contextIds[0];
					if (
						!contextAri ||
						!relayEnvironment ||
						!Array.isArray(data.logs) ||
						data.logs.length === 0
					) {
						return;
					}

					tracing?.recordBridgeCall('emitFrontendLogs - start');

					try {
						await emitFrontendLogs({
							relayEnvironment,
							contextAri,
							appId,
							installationId: extension.installationId,
							extensionId: extension.id,
							logs: data.logs,
						});
					} catch {
						// Silent fail: emitting logs must never surface an error to the app.
					}

					tracing?.recordBridgeCall('emitFrontendLogs - end');
				},

				onNavigate: navigation.onNavigate,
				onThreeLO: async (authUrl, retry: InvokeRetry) => {
					return new Promise((resolve, reject) => {
						invokeRetriesRef.current.push(retry);

						tracing?.recordConsentFlow('consent flow - start');

						setThreeLOInfo({
							authUrl,
							onSuccess: () => {
								tracing?.recordConsentFlow('consent flow - end', 'success');
								resolve();
								setThreeLOInfo(null);
								setIsThreeLOPromptVisible(false);
								setShouldShowRenderer?.(true);

								return Promise.all(invokeRetriesRef.current.map((retry) => retry())).then(() => {
									invokeRetriesRef.current = [];
								});
							},
							onReject: () => {
								tracing?.recordConsentFlow('consent flow - end', 'rejected');
								return reject();
							},
						});
						setIsThreeLOPromptVisible(true);
						setShouldShowRenderer?.(false);
					});
				},
				getContext: async () => ({
					...productContext.viewContext,
				}),
				fetchProduct: async ({ restPath, product, fetchRequestInit, isMultipartFormData }) => {
					const response = await productFetchClient(
						restPath,
						product,
						fetchRequestInit,
						isMultipartFormData,
					);

					const { status, statusText } = response;
					const headers = Object.fromEntries((response.headers as any).entries());
					const isAttachment = isBinaryContentType(response.headers.get('content-type'));
					const body = response.body ? await response.text() : undefined;

					return { headers, status, statusText, body, isAttachment };
				},
				fetchRemote: async ({ remoteKey, fetchRequestInit, isMultipartFormData }) => {
					const { path, ...init } = fetchRequestInit;
					const contextToken = await getContextTokenWrapper();

					const response = await fetchRemoteClient(
						remoteKey,
						init,
						isMultipartFormData,
						path,
						contextToken,
					);

					const { status, statusText } = response;
					const headers = Object.fromEntries((response.headers as any).entries());
					const isAttachment = isBinaryContentType(response.headers.get('content-type'));
					const body = response.body ? await response.text() : undefined;

					return { headers, status, statusText, body, isAttachment };
				},
				requestTeamworkGraph: async (
					payload: RequestTeamworkGraphPayload,
				): Promise<RequestTeamworkGraphResponse> => {
					// NOTE : The bridge v2 method is to be used as the source of truth for this implementation.
					const teamworkGraphSpecificParams = {
						path: TEAMWORK_GRAPH_GATEWAY_PATH,
						product: 'teamwork-graph' as const,
						fetchRequestInit: {
							method: 'POST',
							headers: {
								'content-type': 'application/json',
								'X-Query-Context': `${contextIds[0]}`,
							},
							body: JSON.stringify(payload),
						},
						isMultipartFormData: false,
					};
					const response = await productFetchClient(
						teamworkGraphSpecificParams.path,
						teamworkGraphSpecificParams.product,
						teamworkGraphSpecificParams.fetchRequestInit,
						teamworkGraphSpecificParams.isMultipartFormData,
					);
					if (!response.ok) {
						const errorText = await response.text();
						throw new Error(
							`Unexpected error encountered in requestTeamworkGraph. Status: ${response.status} ${response.statusText}. Response: ${errorText}`,
						);
					}

					const { data, errors } = await response.json();
					return { data, errors };
				},
				...(fg('forge-ui-fetch-user-recommendations-bridge-method')
					? {
							fetchUserRecommendations: async (
								payload: FetchUserRecommendationsPayload,
							): Promise<FetchUserRecommendationsResponse> =>
								fetchUserRecommendations(
									{ contextIds, viewContext: productContext.viewContext },
									payload,
								),
						}
					: {}),
				getFrameId: () => frameId ?? null,
				subscribeRealtimeChannel: async (payload: SubscribeRealtimeChannelPayload) => {
					return subscribeRealtimeChannel(payload);
				},
				publishRealtimeChannel: async (payload: PublishRealtimeChannelPayload) => {
					const contextToken = await getContextTokenWrapper();

					return publishRealtimeChannel(payload, contextToken);
				},
				emitPublic: emitPublicEvent,
				...permissionFunctions,
			},
			customBridgeMethods,
			onLoad,
			isResizable,
			height,
			environment,
			extension,
			iframeRef,
			contextIds,
			isInModal,
			localId: coreData.localId,
			isEmbeddedMacro,
			embeddedMacroParentType,
			embeddedParentLocalId,
			extensionPayload,
			extensionData,
		});

		useEffect(() => {
			setIsLoading?.(loading);
		}, [loading, setIsLoading]);

		const { containerRef } = useResizeAnalytics({
			// need to check if the extension is custom UI as well since this component is used in WebRuntime for UI Kit
			isEnabled: isCustomUI(extension, entryPoint) && fg('forge-ui-app-perf-tracking-enabled'),
		});

		const sandboxAttributeRules = [
			'allow-downloads',
			'allow-forms',
			'allow-modals',
			'allow-pointer-lock',
			'allow-same-origin',
			'allow-scripts',
		];

		try {
			const egressService = egressPermissionService('FETCH_CLIENT_SIDE', extension.egress);
			const containsWildCardEgress = egressService.containsWildCardEgress();

			if (containsWildCardEgress) {
				sandboxAttributeRules.push('allow-popups', 'allow-popups-to-escape-sandbox');
			}
			// eslint-disable-next-line no-unused-vars
		} catch (_) {
			// swallow error
		}
		if (extension.sandboxPolicies?.includes('allow_storage_access_by_user_activation')) {
			sandboxAttributeRules.push('allow-storage-access-by-user-activation');
		}

		let framePermissionsPolicy = 'camera; clipboard-write; display-capture; fullscreen; microphone';
		if (fg('platform_forge-ui_frame-allow-local-network-access')) {
			framePermissionsPolicy += '; local-network-access';
		}

		return (
			<Box
				ref={containerRef}
				xcss={styles.box}
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					height: isResizable || threeLOInfo ? undefined : height,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					width,
				}}
			>
				{navigation.getModalJsx()}
				{permissions.getModalJsx()}
				{(loading || threeLOInfo) && (
					<Box xcss={styles.threeLOInfo}>
						{loading && !threeLOInfo && !isHidden && getLoadingComponent(loadingComponent)}
						{threeLOInfo &&
							ThreeLOPrompt({
								authUrl: threeLOInfo.authUrl,
								onSuccess: threeLOInfo.onSuccess,
								metadata: {
									moduleType: extension.type,
								},
							})}
					</Box>
				)}
				{!userConsentRequired && (
					<Fragment>
						<iframe
							data-testid="hosted-resources-iframe"
							data-forge-iframe
							css={[
								iframeStyles,
								(loading || threeLOInfo || isHidden) && hiddenIframeStyles,
								isInModal && modalIframeStyles,
								isInModal && !fg('forge-ui-modal-rounded-corners') && legacyModalRadiusStyles,
								!isResizable && nonResizeableIframeStyles,
							]}
							sandbox={sandboxAttributeRules.join(' ')}
							allow={framePermissionsPolicy}
							src={src}
							ref={mergedIframeRef}
							onLoad={onLoadIframeFromProps}
							title={`Embedded app content for ${extension.properties?.name ?? appId}`}
						/>
					</Fragment>
				)}
			</Box>
		);
	},
);
